import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Loader2,
  Link2,
  Link2Off,
  RefreshCw,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Clock,
  Eye,
  EyeOff,
  Settings,
  Zap,
  Lock,
  ExternalLink,
} from 'lucide-react';
import { toast } from 'sonner';
import { serviceTitanAPI } from '../../lib/servicetitanApi';
import { warehouseAPI } from '../../lib/api';
import { formatDistanceToNow } from 'date-fns';
import { EntityMappingWizard, MappingStatusCard } from './EntityMappingWizard';

export const ServiceTitanSettings = () => {
  // Connection state
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  
  // Form state
  const [showConnectDialog, setShowConnectDialog] = useState(false);
  const [showDisconnectDialog, setShowDisconnectDialog] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [credentials, setCredentials] = useState({
    st_tenant_id: '',
    client_id: '',
    client_secret: '',
  });
  
  // Settings state
  const [settings, setSettings] = useState({
    sync_materials: true,
    sync_warehouses: true,
    sync_technicians: true,
    sync_trucks: true,
    sync_jobs: false,
    default_warehouse_id: null,
    use_tech_trucks_as_default_location: true,
  });
  
  // Warehouses for default selection
  const [warehouses, setWarehouses] = useState([]);
  
  // Sync history
  const [syncHistory, setSyncHistory] = useState([]);
  const [showSyncHistory, setShowSyncHistory] = useState(false);
  
  // Mapping wizard state
  const [pendingMappingsCount, setPendingMappingsCount] = useState(0);
  const [showMappingWizard, setShowMappingWizard] = useState(false);
  const [wizardAutoOpened, setWizardAutoOpened] = useState(false);

  // Load pending mappings count silently
  const loadPendingMappingsCount = useCallback(async (shouldAutoOpen = false) => {
    try {
      const response = await serviceTitanAPI.getPendingMappings();
      const data = response.data || { technicians: [], trucks: [] };
      const count = (data.technicians?.length || 0) + (data.trucks?.length || 0);
      setPendingMappingsCount(count);
      
      // Auto-open wizard if there are pending mappings and we haven't auto-opened yet
      if (count > 0 && shouldAutoOpen) {
        setShowMappingWizard(true);
        setWizardAutoOpened(true);
      }
    } catch (error) {
      // Silent failure - don't show toast for background load
      console.error('Failed to load pending mappings count:', error);
    }
  }, []);

  // Load status - silent mode (no error toasts on page load)
  const loadStatus = useCallback(async (showErrors = false) => {
    try {
      setLoading(true);
      const response = await serviceTitanAPI.getStatus();
      setStatus(response.data);
      
      // Update settings from status
      if (response.data) {
        setSettings({
          sync_materials: response.data.sync_materials,
          sync_warehouses: response.data.sync_warehouses,
          sync_technicians: response.data.sync_technicians,
          sync_trucks: response.data.sync_trucks,
          sync_jobs: response.data.sync_jobs,
          default_warehouse_id: response.data.default_warehouse_id || null,
          use_tech_trucks_as_default_location: response.data.use_tech_trucks_as_default_location,
        });
        
        // Check for pending mappings
        if (response.data.is_connected) {
          loadPendingMappingsCount(!wizardAutoOpened);
        }
      }
    } catch (error) {
      console.error('Failed to load ServiceTitan status:', error);
      // Only show error toast if explicitly requested (user action)
      if (showErrors) {
        toast.error('Failed to load integration status');
      }
    } finally {
      setLoading(false);
    }
  }, [loadPendingMappingsCount, wizardAutoOpened]);

  useEffect(() => {
    loadStatus(false); // Silent mode on initial load
    loadWarehouses();
  }, [loadStatus]);

  const loadWarehouses = async () => {
    try {
      const response = await warehouseAPI.listWarehouses();
      setWarehouses(response.data || []);
    } catch (error) {
      console.error('Failed to load warehouses:', error);
    }
  };

  const loadSyncHistory = async () => {
    try {
      const response = await serviceTitanAPI.getSyncHistory(10);
      setSyncHistory(response.data || []);
    } catch (error) {
      console.error('Failed to load sync history:', error);
    }
  };

  const handleConnect = async () => {
    if (!credentials.st_tenant_id || !credentials.client_id || !credentials.client_secret) {
      toast.error('Please fill in all credential fields');
      return;
    }

    try {
      setConnecting(true);
      await serviceTitanAPI.connect(credentials);
      toast.success('ServiceTitan connected successfully');
      setShowConnectDialog(false);
      setCredentials({ st_tenant_id: '', client_id: '', client_secret: '' });
      setWizardAutoOpened(false); // Reset so wizard can auto-open for new connection
      await loadStatus(true);
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to connect to ServiceTitan';
      toast.error(message);
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      setDisconnecting(true);
      await serviceTitanAPI.disconnect();
      toast.success('ServiceTitan disconnected');
      setShowDisconnectDialog(false);
      setPendingMappingsCount(0);
      await loadStatus(true);
    } catch (error) {
      toast.error('Failed to disconnect ServiceTitan');
    } finally {
      setDisconnecting(false);
    }
  };

  const handleSync = async (fullSync = false) => {
    try {
      setSyncing(true);
      const response = await serviceTitanAPI.triggerSync({ full_sync: fullSync });
      toast.success('Sync completed', {
        description: `Processed: ${JSON.stringify(response.data.results)}`
      });
      await loadStatus(true);
    } catch (error) {
      const message = error.response?.data?.detail || 'Sync failed';
      toast.error(message);
    } finally {
      setSyncing(false);
    }
  };

  const handleSettingsChange = async (key, value) => {
    const newSettings = { ...settings, [key]: value };
    setSettings(newSettings);
    
    try {
      setSavingSettings(true);
      await serviceTitanAPI.updateSettings({ [key]: value });
      toast.success('Settings updated');
    } catch (error) {
      toast.error('Failed to update settings');
      // Revert on error
      setSettings(settings);
    } finally {
      setSavingSettings(false);
    }
  };

  const getSyncStatusBadge = () => {
    if (!status) return null;
    
    const statusMap = {
      idle: { variant: 'secondary', icon: Clock, label: 'Idle' },
      syncing: { variant: 'default', icon: RefreshCw, label: 'Syncing...' },
      error: { variant: 'destructive', icon: XCircle, label: 'Error' },
      disabled: { variant: 'outline', icon: Link2Off, label: 'Disabled' },
    };
    
    const config = statusMap[status.sync_status] || statusMap.idle;
    const Icon = config.icon;
    
    return (
      <Badge variant={config.variant} className="gap-1">
        <Icon className={`w-3 h-3 ${status.sync_status === 'syncing' ? 'animate-spin' : ''}`} />
        {config.label}
      </Badge>
    );
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Connection Status Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                <Zap className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <CardTitle>ServiceTitan Integration</CardTitle>
                <CardDescription>
                  Sync materials, technicians, trucks, and jobs from ServiceTitan
                </CardDescription>
              </div>
            </div>
            {status?.is_connected && getSyncStatusBadge()}
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Demo Mode Indicator */}
          {status?.mode === 'demo' && (
            <Alert className="bg-amber-50 border-amber-200">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-800">
                <span className="font-medium">Demo Mode Active</span> — Using mock data. No real ServiceTitan API calls are made.
                {status?.read_only && (
                  <span className="block text-xs mt-1 text-amber-700">
                    Read-only: {status.read_only_reason || 'V1 integration is pull-only'}
                  </span>
                )}
              </AlertDescription>
            </Alert>
          )}
          
          {/* Connection Status */}
          <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
            <div className="flex items-center gap-3">
              {status?.is_connected ? (
                <CheckCircle2 className="w-5 h-5 text-green-600" />
              ) : (
                <Link2Off className="w-5 h-5 text-muted-foreground" />
              )}
              <div>
                <p className="font-medium">
                  {status?.is_connected ? 'Connected' : 'Not Connected'}
                </p>
                {status?.is_connected && status?.st_tenant_id && (
                  <p className="text-sm text-muted-foreground">
                    Tenant ID: {status.st_tenant_id}
                  </p>
                )}
                {status?.connection_verified_at && (
                  <p className="text-xs text-muted-foreground">
                    Verified {formatDistanceToNow(new Date(status.connection_verified_at))} ago
                  </p>
                )}
              </div>
            </div>
            <div className="flex gap-2">
              {status?.is_connected ? (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleSync(false)}
                    disabled={syncing || status?.sync_status === 'syncing'}
                  >
                    {syncing ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <RefreshCw className="w-4 h-4 mr-2" />
                    )}
                    Sync Now
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => setShowDisconnectDialog(true)}
                  >
                    <Link2Off className="w-4 h-4 mr-2" />
                    Disconnect
                  </Button>
                </>
              ) : (
                <Button onClick={() => setShowConnectDialog(true)}>
                  <Link2 className="w-4 h-4 mr-2" />
                  Connect ServiceTitan
                </Button>
              )}
            </div>
          </div>

          {/* Error Display */}
          {status?.last_sync_error && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                Last sync error: {status.last_sync_error}
              </AlertDescription>
            </Alert>
          )}

          {/* Last Sync Info */}
          {status?.is_connected && status?.last_sync_at && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Clock className="w-4 h-4" />
              Last synced {formatDistanceToNow(new Date(status.last_sync_at))} ago
              <Button
                variant="link"
                size="sm"
                className="p-0 h-auto"
                onClick={() => {
                  setShowSyncHistory(true);
                  loadSyncHistory();
                }}
              >
                View History
              </Button>
            </div>
          )}

          {/* Mapping Status Card - Replaces old alert */}
          {status?.is_connected && (
            <MappingStatusCard 
              pendingCount={pendingMappingsCount}
              onOpenWizard={() => setShowMappingWizard(true)}
            />
          )}
        </CardContent>
      </Card>

      {/* Sync Settings Card - Only show when connected */}
      {status?.is_connected && (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-muted-foreground" />
              <CardTitle className="text-lg">Sync Settings</CardTitle>
            </div>
            <CardDescription>
              Choose which data to sync from ServiceTitan
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Domain Toggles */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="sync_materials">Materials / Pricebook</Label>
                  <p className="text-sm text-muted-foreground">Sync materials as inventory items</p>
                </div>
                <Switch
                  id="sync_materials"
                  checked={settings.sync_materials}
                  onCheckedChange={(v) => handleSettingsChange('sync_materials', v)}
                  disabled={savingSettings}
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="sync_warehouses">Warehouses</Label>
                  <p className="text-sm text-muted-foreground">Sync warehouse locations</p>
                </div>
                <Switch
                  id="sync_warehouses"
                  checked={settings.sync_warehouses}
                  onCheckedChange={(v) => handleSettingsChange('sync_warehouses', v)}
                  disabled={savingSettings}
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="sync_technicians">Technicians</Label>
                  <p className="text-sm text-muted-foreground">Sync technicians (matched by email)</p>
                </div>
                <Switch
                  id="sync_technicians"
                  checked={settings.sync_technicians}
                  onCheckedChange={(v) => handleSettingsChange('sync_technicians', v)}
                  disabled={savingSettings}
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="sync_trucks">Trucks / Vehicles</Label>
                  <p className="text-sm text-muted-foreground">Sync trucks (requires manual mapping)</p>
                </div>
                <Switch
                  id="sync_trucks"
                  checked={settings.sync_trucks}
                  onCheckedChange={(v) => handleSettingsChange('sync_trucks', v)}
                  disabled={savingSettings}
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="sync_jobs">Jobs</Label>
                  <p className="text-sm text-muted-foreground">Sync jobs for material tracking (read-only)</p>
                </div>
                <Switch
                  id="sync_jobs"
                  checked={settings.sync_jobs}
                  onCheckedChange={(v) => handleSettingsChange('sync_jobs', v)}
                  disabled={savingSettings}
                />
              </div>
            </div>

            {/* Divider */}
            <div className="border-t pt-4">
              <h4 className="font-medium mb-4">Configuration</h4>
              
              {/* Default Warehouse */}
              <div className="space-y-2">
                <Label htmlFor="default_warehouse">Default Warehouse for New Materials</Label>
                <Select
                  value={settings.default_warehouse_id || 'none'}
                  onValueChange={(v) => handleSettingsChange('default_warehouse_id', v === 'none' ? null : v)}
                  disabled={savingSettings}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select warehouse" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No default</SelectItem>
                    {warehouses.map((wh) => (
                      <SelectItem key={wh.id} value={wh.id}>
                        {wh.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  New materials synced from ServiceTitan will be added to this warehouse
                </p>
              </div>
            </div>

            {/* Bidirectional Sync (Locked) */}
            <div className="border-t pt-4">
              <div className="flex items-center justify-between opacity-60">
                <div>
                  <div className="flex items-center gap-2">
                    <Label>Bidirectional Material Sync</Label>
                    <Badge variant="outline" className="gap-1">
                      <Lock className="w-3 h-3" />
                      Coming Soon
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Push material usage back to ServiceTitan jobs
                  </p>
                </div>
                <Switch disabled checked={false} />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Connect Dialog */}
      <Dialog open={showConnectDialog} onOpenChange={setShowConnectDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Connect ServiceTitan</DialogTitle>
            <DialogDescription>
              Enter your ServiceTitan API credentials to connect your account.
              <a
                href="https://developer.servicetitan.io/"
                target="_blank"
                rel="noopener noreferrer"
                className="ml-1 inline-flex items-center text-primary hover:underline"
              >
                Get credentials <ExternalLink className="w-3 h-3 ml-1" />
              </a>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="st_tenant_id">ServiceTitan Tenant ID</Label>
              <Input
                id="st_tenant_id"
                placeholder="123456789"
                value={credentials.st_tenant_id}
                onChange={(e) => setCredentials({ ...credentials, st_tenant_id: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="client_id">Client ID</Label>
              <Input
                id="client_id"
                placeholder="cid.xxxxxxxx"
                value={credentials.client_id}
                onChange={(e) => setCredentials({ ...credentials, client_id: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="client_secret">Client Secret</Label>
              <div className="relative">
                <Input
                  id="client_secret"
                  type={showSecret ? 'text' : 'password'}
                  placeholder="cs.xxxxxxxx"
                  value={credentials.client_secret}
                  onChange={(e) => setCredentials({ ...credentials, client_secret: e.target.value })}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3"
                  onClick={() => setShowSecret(!showSecret)}
                >
                  {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConnectDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleConnect} disabled={connecting}>
              {connecting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Link2 className="w-4 h-4 mr-2" />
              )}
              Connect
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Disconnect Confirmation Dialog */}
      <Dialog open={showDisconnectDialog} onOpenChange={setShowDisconnectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Disconnect ServiceTitan?</DialogTitle>
            <DialogDescription>
              This will stop all syncing with ServiceTitan. Your existing data will be preserved.
              You can reconnect at any time.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDisconnectDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDisconnect} disabled={disconnecting}>
              {disconnecting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Link2Off className="w-4 h-4 mr-2" />
              )}
              Disconnect
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sync History Dialog */}
      <Dialog open={showSyncHistory} onOpenChange={setShowSyncHistory}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Sync History</DialogTitle>
            <DialogDescription>Recent sync operations</DialogDescription>
          </DialogHeader>
          <div className="max-h-[400px] overflow-y-auto">
            {syncHistory.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">
                No sync history yet
              </p>
            ) : (
              <div className="space-y-2">
                {syncHistory.map((log) => (
                  <div
                    key={log.id}
                    className="p-3 border rounded-lg text-sm space-y-1"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Badge variant={log.status === 'success' ? 'secondary' : 'destructive'}>
                          {log.status}
                        </Badge>
                        <span className="font-medium capitalize">{log.domain}</span>
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {formatDistanceToNow(new Date(log.started_at))} ago
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {log.sync_type} • {log.initiated_by} •
                      {log.records_created > 0 && ` ${log.records_created} created`}
                      {log.records_updated > 0 && ` ${log.records_updated} updated`}
                      {log.duration_ms && ` • ${(log.duration_ms / 1000).toFixed(1)}s`}
                    </div>
                    {log.error_message && (
                      <p className="text-xs text-destructive">{log.error_message}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Entity Mapping Wizard */}
      <EntityMappingWizard
        open={showMappingWizard}
        onClose={() => setShowMappingWizard(false)}
        onComplete={() => {
          setPendingMappingsCount(0);
          loadPendingMappingsCount(false);
        }}
      />
    </div>
  );
};

export default ServiceTitanSettings;
